import {
  createContext,
  useState,
  useMemo,
  useContext,
  useCallback
} from 'react';
import { useTheme } from '@material-ui/styles';
import { DateTime } from 'luxon';
import { useMeContext } from '~/modules/me/useMeContext';
import useThemeBreakpoint from '~/modules/common/hooks/useThemeBreakpoint';
import {
  PERIOD_SCALE_ENUM,
  DEFAULT_PERIOD_SCALE
} from '~/modules/resourcing/common/enums';
import { scaleOffsets } from '~/modules/resource-management/ResourcingOverview/util';
import useDateState from '~/modules/common/charts/timeline/calculations/useDateState';
import { useResourceOverviewChartSettingsMutation } from './useResourceOverviewChartSettingsMutation';

export const weekUris = [
  'urn:replicon:day-of-week:monday',
  'urn:replicon:day-of-week:tuesday',
  'urn:replicon:day-of-week:wednesday',
  'urn:replicon:day-of-week:thursday',
  'urn:replicon:day-of-week:friday',
  'urn:replicon:day-of-week:saturday',
  'urn:replicon:day-of-week:sunday'
];

export const ResourceOverviewToolbarContext = createContext();

export const useResourceOverviewToolbarContext = () =>
  useContext(ResourceOverviewToolbarContext);

export const useResourceOverviewToolbarContextValue = ({
  chartSettings
} = {}) => {
  const theme = useTheme();
  const me = useMeContext();
  const {
    workWeekStartDay: { uri: workWeekStartDayUri },
    featureFlags: { isRmp2025Q3Enhancements }
  } = me;

  const breakPoint = useThemeBreakpoint(theme);
  const [periodScale, setPeriodScale] = useState(
    chartSettings?.scale || DEFAULT_PERIOD_SCALE
  );

  const [startDate, setStartDate] = useDateState({
    initialValue: DateTime.utc(),
    sessionStorageKey: isRmp2025Q3Enhancements
      ? 'resource-overview-chart-start-date'
      : undefined
  });

  const dateRange = useMemo(
    () =>
      getDateRange({
        date: startDate,
        scale: periodScale,
        workWeekStartDayUri,
        breakPoint
      }),
    [startDate, periodScale, workWeekStartDayUri, breakPoint]
  );

  const { putChartSettings } = useResourceOverviewChartSettingsMutation();

  const setScale = useCallback(
    scale => {
      setPeriodScale(scale);
      if (isRmp2025Q3Enhancements) {
        putChartSettings({ scale });
      }
    },
    [isRmp2025Q3Enhancements, putChartSettings]
  );

  const setNextPeriod = useCallback(() => {
    const offsetToApply = scaleOffsets[periodScale];
    const startDateWithOffset = dateRange.startDate.plus(offsetToApply);

    setStartDate(startDateWithOffset);
  }, [dateRange, periodScale, setStartDate]);

  const setPreviousPeriod = useCallback(() => {
    const offsetToApply = scaleOffsets[periodScale];
    const startDateWithOffset = dateRange.startDate.minus(offsetToApply);

    setStartDate(startDateWithOffset);
  }, [dateRange, periodScale, setStartDate]);

  const setDate = useCallback(
    newDate => {
      setStartDate(newDate);
    },
    [setStartDate]
  );

  return useMemo(
    () => ({
      periodScale,
      setScale,
      dateRange,
      setNextPeriod,
      setPreviousPeriod,
      setDate
    }),
    [
      dateRange,
      periodScale,
      setNextPeriod,
      setPreviousPeriod,
      setScale,
      setDate
    ]
  );
};

const getChartPeriodCount = breakPoint => {
  switch (breakPoint) {
    case 'xl':
      return 10;
    case 'lg':
      return 8;
    case 'md':
      return 6;
    case 'sm':
      return 5;
    default:
      return 3;
  }
};

const getDateRangeWithoutTimeStamp = ({ startDate, endDate }) => ({
  startDate: DateTime.utc(
    startDate.year,
    startDate.month,
    startDate.day,
    0,
    0,
    0,
    0
  ),
  endDate: DateTime.utc(endDate.year, endDate.month, endDate.day, 0, 0, 0, 0)
});

const getDateRange = ({ date, scale, workWeekStartDayUri, breakPoint }) => {
  switch (scale) {
    case PERIOD_SCALE_ENUM.WEEKS: {
      const tempStartDate = date.set({
        weekday: weekUris.indexOf(workWeekStartDayUri) + 1
      });

      const startDate =
        tempStartDate > date
          ? tempStartDate.minus({ weeks: 1 })
          : tempStartDate;
      const endDate = startDate
        .plus({
          week: getChartPeriodCount(breakPoint)
        })
        .minus({ days: 1 });

      return getDateRangeWithoutTimeStamp({ startDate, endDate });
    }
    case PERIOD_SCALE_ENUM.MONTHS: {
      const startDate = date.startOf('month');
      const endDate = startDate
        .plus({
          month: getChartPeriodCount(breakPoint)
        })
        .endOf('month');

      return getDateRangeWithoutTimeStamp({ startDate, endDate });
    }
    case PERIOD_SCALE_ENUM.YEARS: {
      const startDate = date.startOf('year');
      const endDate = startDate.endOf('year').plus({
        year: getChartPeriodCount(breakPoint)
      });

      return getDateRangeWithoutTimeStamp({ startDate, endDate });
    }
    case PERIOD_SCALE_ENUM.QUARTERS: {
      const startDate = date.startOf('quarter');
      const endDate = startDate.endOf('quarter').plus({
        quarters: getChartPeriodCount(breakPoint)
      });

      return getDateRangeWithoutTimeStamp({ startDate, endDate });
    }
    default: {
      const startDate = date.startOf('day');
      const endDate = startDate.plus({
        days: getChartPeriodCount(breakPoint)
      });

      return getDateRangeWithoutTimeStamp({ startDate, endDate });
    }
  }
};

export default useResourceOverviewToolbarContext;
